import React, { memo, ReactNode } from "react";
import { connect } from "~/redux/connect";
import { getEditFields, getField, getFieldGridControlSources, getFieldValue } from "~/stateGetter";

import identify from "~/identify";

import { modelActionsCreator } from '~/actions/model_actions';
import { createTabRow, createBigRadio } from "./widgetFactory";

export type WidgetNode = {
    title: string;
    icon?: string;
    iconFamily?: string;
    key: string | number;
    children: ReactNode;
    description?: string;
    disabled?: boolean;
    variant?: string;
    value?: string | number;
    isWidgetGroup?: false;
}

export type WidgetGroupNode = {
    title: string;
    icon?: string;
    iconFamily?: string;
    key: string | number;
    description?: string;
    nodes: WidgetNode[];
    isWidgetGroup: true;
}


export type WidgetRowProps = {
    widgetType: string;
    nodes: (WidgetNode | WidgetGroupNode)[];
    defaultValue?: string | number;
    modelId: string;
    view: boolean;

    lastSavedAt?: number | null;
    displayAs?: string;
    widgetName?: string
    referenceField?: string;
    value?: string | number;
}



const widgetMapping = {
    tabs: createTabRow({variant: "tabs"}),
    wizard: createTabRow({variant: "wizard"}),
    big_radio: createBigRadio(),
    big_checkbox: createBigRadio({ multiple: true }),
}


export const WidgetRow = connect(
    mapStateToWidgetRowProps,
    mapDispatchToProps,
    mergeProps,
    {
        areStatePropsEqual: (before, after, key) => {
            if (key === 'errorNodes') return new Set([...before.errorNodes, ...after.errorNodes]).size === after.errorNodes.length;
            return Object.is(before, after)
        }
    }
)(memo(((props: WidgetRowProps) => {
    const Widget = widgetMapping[props.widgetType]?.Component;

    if (!Widget) return null;
    return <Widget {...props} />
    
})));


function mapStateToWidgetRowProps(state, props: WidgetRowProps) { 
    const {modelId, widgetType, nodes, referenceField} = props;
    const editFields = getEditFields(state, modelId);

    const errorGroups = Object.values(editFields)
        .reduce((errorState: Record<string, boolean>, field: { group: string, name: string}) => {
            if (errorState[field.group]) return errorState;
            const hasError = !!getField(state, modelId, identify.EDITFIELD, field.name).validationMessage
            return {
                ...errorState,
                [field.group]: hasError,
            }
        }, {} );
    
    const referenceFieldValue = getFieldValue(state, modelId, identify.EDITFIELD, referenceField)?.value;

    const gridSources = getFieldGridControlSources(state, modelId);

    const stateProps = ({
        value: referenceFieldValue,
        errorNodes: nodes
            .filter((node) => {
                const sources = node.isWidgetGroup ? node.nodes.map((subnode) => gridSources[subnode.key]).flat() : gridSources[node.key];
                return !!sources?.some((fieldGroup) => !!errorGroups[fieldGroup]);
            })
            .map((node) => node.key),
    })

    return widgetMapping[widgetType]?.mapStateToProps?.(state, props, stateProps) ?? stateProps
}

function mapDispatchToProps(dispatch, props: WidgetRowProps) {
    const modelActions = modelActionsCreator(props.modelId);
    const actions = props.referenceField ? ({
        onChange: (value) => dispatch(modelActions.changeFieldValue(props.referenceField, identify.EDITFIELD, value)),
    }) : {};

    return {
        ...actions,
        ...widgetMapping[props.widgetType]?.mapDispatchToProps?.(dispatch, props)
    };
}

function mergeProps(state, dispatch, props: WidgetRowProps) {
    return widgetMapping[props.widgetType]?.mergeProps?.(state, dispatch, props) ?? {...state, ...dispatch, ...props};
}
